import { createSignal, onCleanup, onMount, Show, type JSX } from "solid-js";

interface LazyRenderProps {
    // Held while the children are not mounted, so scrolling past an unrendered block does not jump.
    // Only used until the block has been measured once; after that its real height is reused.
    estimatedHeight: number;
    rootMargin?: string;
    children: () => JSX.Element;
}

// Keeps a tall block out of the DOM until it is near the viewport. A page listing several shops'
// full stock puts thousands of item tiles on screen at once, of which two cards' worth are ever
// visible; the rest cost layout, sprite decodes and reactive computations for nothing.
export default function LazyRender(props: LazyRenderProps) {
    const [mounted, setMounted] = createSignal(false);
    const [measuredHeight, setMeasuredHeight] = createSignal<number | null>(null);
    let host!: HTMLDivElement;

    onMount(() => {
        if (typeof IntersectionObserver === "undefined") {
            setMounted(true);
            return;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                const near = entries.some((entry) => entry.isIntersecting);
                if (!near && mounted()) setMeasuredHeight(host.getBoundingClientRect().height);
                setMounted(near);
            },
            // Wide enough that a block is ready before it scrolls in, and that scrolling back a little
            // does not unmount what was just rendered.
            { rootMargin: props.rootMargin ?? "1000px" },
        );
        observer.observe(host);
        onCleanup(() => observer.disconnect());
    });

    const placeholderHeight = () => `${measuredHeight() ?? props.estimatedHeight}px`;

    return (
        <div ref={host} style={mounted() ? undefined : { height: placeholderHeight() }}>
            <Show when={mounted()}>{props.children()}</Show>
        </div>
    );
}
